import { NULL_AS_STRING } from '@repo/shared/dtos/beneficiary'
import { NumberSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'

import { BaseForm } from '@/components/admin/structure-data/schema/forms/BaseForm'
import {
  NumberFormValues,
  numberNullToString,
  numberValidationSchema,
  stringToNumberNull,
} from '@/components/admin/structure-data/schema/forms/validation'

export function ItemNumberForm({
  item,
  onUpdate,
}: {
  item: NumberSchemaItem
  onUpdate: (item: NumberSchemaItem) => void
}) {
  const { tc } = useT()
  const defaultValues = useMemo(
    () =>
      ({
        label: item.label,
        instructionsToFillValue: item.instructionsToFillValue,
        defaultValue: numberNullToString(item.defaultValue),
        type: 'number',
      }) satisfies NumberFormValues,
    [item],
  )

  const handleUpdate = (values: NumberFormValues) => {
    onUpdate({ ...item, ...values, defaultValue: stringToNumberNull(values.defaultValue) })
  }

  return (
    <BaseForm defaultValues={defaultValues} onUpdate={handleUpdate} schema={numberValidationSchema}>
      {(formApi) => (
        <FormField
          control={formApi.control}
          name="defaultValue"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {tc('defaultValue', { cfl: true })} ({tc('optional')})
              </FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <Input
                    {...field}
                    id="defaultValue"
                    placeholder={tc('defaultValue', { cfl: true })}
                    type="number"
                  />
                  <TooltipWrapper content={tc('delete', { cfl: true })}>
                    <Button
                      variant="outline"
                      onClick={() => field.onChange(NULL_AS_STRING, { shouldDirty: true })}
                      size="icon"
                      disabled={field.value === NULL_AS_STRING}
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  </TooltipWrapper>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </BaseForm>
  )
}
